import React, { useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

// --- Context and Hook Imports ---
import { useAuth, AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import useErrorHandler from "./hooks/useErrorHandler";

// --- Component Imports ---
import Sidebar from "./components/Sidebar/Sidebar";
import SignIn from "./Login";
import Dashboard from "./Pages/Dashboard/Dashboard";
import Ess from "./Pages/Ess/ESS";
import ComingSoon from "./components/ComingSoon/ComingSoon";
import Navbar from "./components/NavBar/Navbar";
import CompensationBenefits from "./Pages/Compensation-Benefits/CompensationBenefits";
import Watermark from "./components/Watermark/Watermark";
import Profile from "./Pages/Profile/Profile";
import Subscriptions from "./Pages/Subscriptions";
import Team from "./Pages/Team/Team";
import AccountSettings from "./Pages/Profile/AccountSettings";
import Error500 from "./Pages/Error500";
import EmailAutomation from "./Pages/Email Automation/EmailAutomation";
import EaSettings from "./Pages/Email Automation/EaSettings";
import LaunchedTemplates from "./Pages/Email Automation/LaunchedTemplates";
import UpgradePlan from "./Pages/UpgradePlan";
import ErrorBoundary from "./ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import ProtectedRoute from "./components/ProtectedRoute";
import MarketplaceSidebar from "./components/MarketplaceSidebar/MarketPlaceSidebar";

// --- Authorization Config Import ---
import { EMAIL_AUTOMATION_AUTH } from "./config/authorizationConfig";

// --- Library Imports ---
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.scss";
import "./styles/AppPageSystem.scss";
import AnalyticsDashboard from "./Pages/Analytics/Analytics";
import UpdatesPage from "./components/NavBar/Updatespage";
import NavigationTour from "./components/NavigationTour/NavigationTour";

const AppLayout = () => {
  // Notification state managed at AppLayout level
  const [notificationCounts, setNotificationCounts] = useState({
    dashboard: 0,
    analytics: 0,
    emailAutomation: 0,
    ess: 0,
    recruitment: 0,
    onboarding: 0,
    performance: 0,
    compensation: 0,
    offboarding: 0,
    teams: 0,
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Handler to update notification counts from NotificationCenter
  const handleNotificationReceived = (counts) => {
    setNotificationCounts(counts);
  };

  return (
    <div className="app">
      {/* Pass the notification handler to Navbar so it can pass to NotificationCenter */}
      <Navbar
        onNotificationReceived={handleNotificationReceived}
        onMenuToggle={() => setMobileNavOpen((open) => !open)}
        isMobileNavOpen={mobileNavOpen}
      />
      <Watermark text="HRLabs.in" />
      <Sidebar
        notificationCounts={notificationCounts}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />
      <NavigationTour />
      {/* Pass notification counts to Sidebar */}
      <MarketplaceSidebar />
      <main className="main-content">
        <Routes>
          {/* All routes inside AppLayout are automatically protected */}
          <Route
            path="/dashboard"
            element={<Dashboard notificationCounts={notificationCounts} />}
          />
          <Route path="/analytics/*" element={<AnalyticsDashboard />} />

          {/* Protected Email Automation Routes */}
          <Route
            path="/email-automation/*"
            element={
              <ProtectedRoute
                authConfig={EMAIL_AUTOMATION_AUTH.VIEW}
                fallbackComponent={
                  <UpgradePlan
                    featureName="Email Automation"
                    description="Access advanced email automation features including scheduled events, cron jobs, and notification management"
                  />
                }
              >
                <EmailAutomation notificationCounts={notificationCounts} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/email-automation/launched-templates"
            element={
              <ProtectedRoute
                authConfig={EMAIL_AUTOMATION_AUTH.VIEW}
                fallbackComponent={
                  <UpgradePlan
                    featureName="Email Automation"
                    description="Access advanced email automation features including scheduled events, cron jobs, and notification management"
                  />
                }
              >
                <LaunchedTemplates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/email-automation/ea-settings"
            element={
              <ProtectedRoute
                authConfig={EMAIL_AUTOMATION_AUTH.VIEW}
                fallbackComponent={
                  <UpgradePlan
                    featureName="Email Automation"
                    description="Access advanced email automation features including scheduled events, cron jobs, and notification management"
                  />
                }
              >
                <EaSettings />
              </ProtectedRoute>
            }
          />
          <Route path="/updates" element={<UpdatesPage />} />

          <Route path="/recruitment" element={<ComingSoon />} />
          <Route path="/onboarding" element={<ComingSoon />} />
          <Route path="/on-boarding" element={<ComingSoon />} />
          <Route path="/performance-management/*" element={<ComingSoon />} />
          <Route
            path="/compensation-benefits/*"
            element={
              <CompensationBenefits notificationCounts={notificationCounts} />
            }
          />
          <Route path="/offboarding/*" element={<ComingSoon />} />
          <Route
            path="/ess/*"
            element={<Ess notificationCounts={notificationCounts} />}
          />
          <Route
            path="/team/*"
            element={<Team notificationCounts={notificationCounts} />}
          />
          <Route path="/profile" element={<Profile />} />
          <Route path="/account-settings" element={<AccountSettings />} />
          <Route path="/subscriptions" element={<Subscriptions />} />
          <Route path="/error-500" element={<Error500 />} />
          {/* A fallback route for any other authenticated paths */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
    </div>
  );
};

function App() {
  // Custom hook to suppress specific console errors
  useErrorHandler();

  return (
    <>
      <Router>
        <AuthProvider>
          <ThemeProvider>
            <ErrorBoundary fallback={<Error500 />}>
              <ScrollToTop />
              <Routes>
                {/* Public Route: The login page */}
                <Route path="/" element={<SignIn />} />

                {/* Protected Routes: All other routes are wrapped */}
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <AppLayout />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </ErrorBoundary>
          </ThemeProvider>
        </AuthProvider>
      </Router>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        draggable
        pauseOnHover
        theme="light"
        style={{
          zIndex: 9999999,
          position: "fixed",
        }}
      />
    </>
  );
}

export default App;
